import {
    BookOpen,
    Save,
    Target,
    Trophy
} from "lucide-react";
import { useState } from "react";
import { useToast } from "../ui/Toast";
import api from "../../services/api";

const DailyReviewForm = ({ onSubmitSuccess, disabled, selectedDate }) => {
  const [formData, setFormData] = useState({
    dailyGoal: "",
    executionScore: 5,
    keyLesson: "",
  });

  const toast = useToast();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
        // Map to JournalEntry fields
        const payload = {
            entryType: "daily", // Match DB constraint
            notes: `[Daily Review]\nGoal: ${formData.dailyGoal}\nLesson: ${formData.keyLesson}`,
            confidenceLevel: formData.executionScore, // Using confidence column for execution score
            entryDate: selectedDate || new Date().toISOString().split('T')[0],
            // Additional semantic fields if schema supports them later:
            // executionQuality: formData.executionScore 
        };

        await api.post("/journal", payload);
        onSubmitSuccess();
        setFormData({ dailyGoal: "", executionScore: 5, keyLesson: "" });
        
    } catch (error) {
        console.error("Daily Review Error:", error);
        toast.error("Failed to save review.");
    } finally {
        setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5 animate-fade-in p-2">
       
       {/* Goal */}
       <div>
          <label className="flex items-center gap-2 text-xs text-indigo-400 uppercase font-semibold mb-2">
             <Target size={14} /> Today's Goal / Focus
          </label>
          <input 
            required
            value={formData.dailyGoal}
            onChange={(e) => setFormData(p => ({ ...p, dailyGoal: e.target.value }))}
            className="w-full bg-slate-900 border border-slate-700 rounded-lg p-3 text-white placeholder-slate-600 focus:border-indigo-500 outline-none"
            placeholder="e.g. Follow my trading plan perfectly..."
          />
       </div>

       {/* Execution Score Slider */}
       <div className="bg-slate-900/50 p-4 rounded-xl border border-slate-800">
           <div className="flex justify-between items-center mb-4">
              <label className="flex items-center gap-2 text-xs text-emerald-400 uppercase font-semibold">
                 <Trophy size={14} /> Execution Score
              </label>
              <span className={`text-xl font-bold ${formData.executionScore >= 8 ? "text-emerald-400" : formData.executionScore >= 5 ? "text-yellow-400" : "text-red-400"}`}>
                 {formData.executionScore}/10
              </span>
           </div>
           
           <input 
              type="range" min="1" max="10" 
              value={formData.executionScore}
              onChange={(e) => setFormData(p => ({ ...p, executionScore: parseInt(e.target.value) }))}
              className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-emerald-500 hover:[&::-webkit-slider-thumb]:scale-110 transition-all"
           />
           <div className="flex justify-between text-[10px] text-slate-500 mt-2 uppercase font-medium">
              <span>Poor</span>
              <span>Average</span>
              <span>Perfect</span>
           </div>
       </div>

       {/* Key Lesson */}
       <div>
          <label className="flex items-center gap-2 text-xs text-amber-400 uppercase font-semibold mb-2">
             <BookOpen size={14} /> Key Lesson Learned
          </label>
          <textarea 
             required
             value={formData.keyLesson}
             onChange={(e) => setFormData(p => ({ ...p, keyLesson: e.target.value }))}
             rows="3"
             className="w-full bg-slate-900 border border-slate-700 rounded-lg p-3 text-white placeholder-slate-600 focus:border-indigo-500 outline-none resize-none"
             placeholder="What did the market teach you today?"
          />
       </div>

       <button 
         type="submit" 
         disabled={loading || disabled}
         className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white font-bold py-3 rounded-xl shadow-lg transform active:scale-95 transition-all flex items-center justify-center gap-2"
       >
          <Save size={18} />
          Save Daily Review
       </button>

    </form>
  );
};

export default DailyReviewForm;
